import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import * as Sentry from '@sentry/angular-ivy';
import { OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { take, takeUntil } from 'rxjs/operators';

import { environment } from 'environments/environment';

import { ComponentBase } from '../component.base';
import { GetClientControlOptionsResponseModel } from '../models/user.models';
import { UserProfile } from '../models/user-profile.models';
import { ErrorService } from './error.service';
import { UserManagementService } from './user-management.service';

@Injectable({
	providedIn: 'root',
})
export class AuthService extends ComponentBase {
	isAuthorized = false;
	userData: UserProfile;
	userControlOptions: GetClientControlOptionsResponseModel;

	openIdConfiguration: OpenIdConfiguration;

	token: string = '';

	private get basePortalUrl(): string {
		return environment.basePortalUrl;
	}

	private get authUrl(): string {
		return environment.authUrl;
	}

	constructor(
		private oidcSecurityService: OidcSecurityService,
		private router: Router,
		private userManagementService: UserManagementService,
		private errorService: ErrorService,
	) {
		super();
	}

	public initAuth() {
		this.openIdConfiguration = {
			configId: 'auth',
			triggerAuthorizationResultEvent: true,
			authority: environment.authUrl,
			redirectUrl: `${environment.basePortalUrl}/auth-callback`,
			clientId: 'wine_portal_client',
			scope: 'openid profile email offline_access wine_portal_api',
			responseType: 'code',
			silentRenew: true,
			silentRenewUrl: `${environment.basePortalUrl}/silent-renew.html`,
			useRefreshToken: true,
			postLoginRoute: '/listings',
			postLogoutRedirectUri: `${environment.basePortalUrl}`,
			forbiddenRoute: '/forbidden',
			unauthorizedRoute: '/unauthorized',
			autoUserInfo: true,
			historyCleanupOff: true,
			renewTimeBeforeTokenExpiresInSeconds: 60,
			authWellknownEndpoints: {
				issuer: environment.authUrl,
				jwksUri: `${environment.authUrl}/.well-known/openid-configuration/jwks`,
				authorizationEndpoint: `${environment.authUrl}/connect/authorize`,
				tokenEndpoint: `${environment.authUrl}/connect/token`,
				userInfoEndpoint: `${environment.authUrl}/connect/userinfo`,
				endSessionEndpoint: `${environment.authUrl}/connect/endsession`,
				checkSessionIframe: `${environment.authUrl}/connect/checksession`,
				revocationEndpoint: `${environment.authUrl}/connect/revocation`,
				introspectionEndpoint: `${environment.authUrl}/connect/introspect`,
			},
			secureRoutes: [
				environment.billingUrl,
				environment.facilityManagementUrl,
				environment.marketplaceUrl,
				environment.paymentsUrl,
				environment.referenceDataUrl,
				environment.storageManagementUrl,
				environment.userManagementUrl,
			],
		};
		this.checkAuth();
	}

	private onAuthorizationResultComplete(isAuthenticated, userData): void {
		if (!isAuthenticated) {
			if (window.parent) {
				// sent from the child iframe, for example the silent renew
				this.router.navigate(['/unauthorized']);
			} else {
				window.location.href = '/unauthorized';
			}
		} else {
			this.mapUserData(userData);
		}
		if (this.router.url.indexOf('/auth-callback') != -1) {
			window.location.href = this.basePortalUrl;
		}
	}

	login(): void {
		if (this.router.url.indexOf('/auth-callback') === -1) {
			this.oidcSecurityService.authorize();
		}
	}

	logout(): void {
		this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
	}

	checkAuth(): void {
		// this.oidcSecurityService
		// 	.checkAuth('', 'auth')
		// 	.pipe(takeUntil(this.ngUnsubscribe$))
		// 	.subscribe({
		// 		next: ({ isAuthenticated, userData, accessToken }) => {
		// 			this.isAuthorized = isAuthenticated;
		// 			this.token = accessToken;
		// 			if (!isAuthenticated && this.router.url.indexOf('/sign-up') != -1) {
		// 				this.login();
		// 			} else {
		// 				this.userData = userData;
		// 				if (userData?.status == 'Pending') {
		// 					this.router.navigate(['/sign-up']);
		// 				}
		// 				this.mapUserData(userData);
		// 			}
		// 		},
		// 		error: (_) => {
		// 			console.log('err');
		// 			window.location.href = this.basePortalUrl;
		// 		},
		// 	});

		this.oidcSecurityService.checkAuth('', 'auth').subscribe(({ isAuthenticated, userData }) => {
			this.oidcSecurityService
				.getAccessToken('auth')
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe({
					next: (token) => {
						this.token = token;
					},
				});
			if (!isAuthenticated && this.router.url.indexOf('/sign-up') != -1) {
				this.login();
			} else {
				this.mapUserData(userData);
			}
		});
	}

	recheckUserControlOptions(): void {
		if (this.userData.account_id) {
			this.userManagementService
				.getClientControlOptionsV1()
				.pipe(takeUntil(this.ngUnsubscribe$))
				.subscribe({
					next: (options) => {
						this.userControlOptions = options;
					},
					error: (err) => {
						console.log(err);
						if (err.status == 0) {
							this.errorService.error = 'Unable to connect to API servers.';
						}
					},
				});
		}
	}

	private mapUserData(userData: any) {
		if (userData) {
			Sentry.setUser({ email: userData.email, id: userData.account_id });
			this.userData = userData;
			if (this.userData.status == 'Pending') {
				this.router.navigate(['/sign-up']);
			}
			if (userData.account_id) {
				this.userManagementService
					.getClientControlOptionsV1()
					.pipe(takeUntil(this.ngUnsubscribe$))
					.subscribe({
						next: (options) => {
							this.userControlOptions = options;
							if (this.router.url.includes('/auth-callback')) {
								this.router.navigate(['/']);
							}
						},
						error: (err) => {
							if (err.status == 0) {
								this.errorService.error = 'Unable to connect to API servers.';
							} else {
								this.errorService.error = 'Server failed to gather user details.';
							}
							this.router.navigate(['/error']);
						},
					});
			}
		}
	}
}
